import { testLatency } from '@/lib/net-speed';
import { WifiIcon, WifiOffIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ConnectionMonitor = (props?: any) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState({
    quality: 'checking',
    latency: null,
    isOnline: navigator.onLine,
  });

  // Monitor connection status
  useEffect(() => {
    const checkConnection = async () => {
      const tThreshold = 150; //ms

      try {
        const latencyAvg = await testLatency();

        let quality: string;

        if (latencyAvg <= tThreshold) {
          quality = 'good';
        } else if (latencyAvg > 300) {
          quality = 'poor';
        } else {
          quality = 'fair';
        }

        setStatus({
          quality,
          latency: latencyAvg,
          isOnline: true,
        });
      } catch (err) {
        setStatus((prev) => ({
          ...prev,
          quality: 'poor',
          isOnline: navigator.onLine,
        }));
      }
    };

    const handleOnline = () =>
      setStatus((prev) => ({ ...prev, isOnline: true }));
    const handleOffline = () =>
      setStatus((prev) => ({ ...prev, isOnline: false }));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const intervalId = setInterval(checkConnection, 1e3 * 10); // Check every 10 seconds
    checkConnection();

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getStatusColor = () => {
    if (!status.isOnline) return 'text-red-500';
    switch (status.quality) {
      case 'good':
        return 'text-green-500';
      case 'fair':
        return 'text-yellow-500';
      case 'poor':
        return 'text-red-500';
      default:
        return 'text-gray-400';
    }
  };

  const getMessage = () => {
    if (!status.isOnline) return t('coach.connection_monitor.messages.offline');
    switch (status.quality) {
      case 'good':
        return t('coach.connection_monitor.messages.good');
      case 'fair':
        return t('coach.connection_monitor.messages.fair');
      case 'poor':
        return t('coach.connection_monitor.messages.poor');
      default:
        return t('coach.connection_monitor.messages.checking');
    }
  };

  return (
    <div className="flex items-center gap-2 mt-4 text-sm" {...props}>
      {status.isOnline ? (
        <WifiIcon className={`h-4 w-4 mr-1 ${getStatusColor()}`} />
      ) : (
        <WifiOffIcon className="h-4 w-4 mr-1 text-red-500" />
      )}
      <span className={`${getStatusColor()} font-medium`}>{getMessage()}</span>
      {status.latency && status.quality !== 'checking' && (
        <span className="text-gray-500 text-xs pl-1">
          ({Math.round(status.latency)} ms)
        </span>
      )}
    </div>
  );
};

export default ConnectionMonitor;
